import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import posthogAnalytics from '../utils/posthogAnalytics';

/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the application,
 * eliminating the need for props drilling.
 */
const AuthContext = createContext(null);

/**
 * Hook to access authentication context
 * @returns {object} - Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 * Wraps the application and provides auth state/methods to all children.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * Verify token and fetch current user
   */
  const verifyAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      setLoading(false);
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }

    try {
      const response = await api.get('/auth/me');
      
      setUser(response.data);
      setIsAuthenticated(true);
      // Identify on session restore too — otherwise returning users (valid token
      // in storage) never enable autocapture until they log in again.
      posthogAnalytics.identifyUser(response.data);
      return true;
    } catch (error) {
      console.error('Auth verification failed:', error);
      // Clear invalid token
      localStorage.removeItem('token');
      setUser(null);
      setIsAuthenticated(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Establish an authenticated session from any login entry point (password,
   * SSO, invite, signup). This is the single funnel: it persists the token/user,
   * identifies the person and emits the `login` analytics event exactly once
   * (before the canonical user is fetched, so it's attributed under
   * `identified_only`), then verifies the token to load the full user
   * (permissions, scope, …) into state. Navigation is the caller's concern.
   *
   * @param {string|null} token JWT to persist (may be null for cookie/redirect SSO).
   * @param {object} [userData] The user object handed back by the auth flow.
   * @returns {Promise<boolean>} Whether the session verified successfully.
   */
  const completeLogin = useCallback(async (token, userData) => {
    // Persist defensively — localStorage can throw in private mode / on quota.
    try {
      if (token) localStorage.setItem('token', token);
      if (userData) localStorage.setItem('user', JSON.stringify(userData));
    } catch (err) {
      console.warn('Failed to persist auth session to localStorage:', err);
    }
    if (userData) {
      // Identify BEFORE emitting `login` so the event is attributed (identified_only).
      posthogAnalytics.identifyUser(userData);
      posthogAnalytics.track('login');
    }
    return verifyAuth();
  }, [verifyAuth]);

  /**
   * Login user with email and password.
   */
  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;
      const verified = await completeLogin(access_token, userData);
      if (!verified) {
        return { success: false, error: 'Could not establish session' };
      }
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  };

  /**
   * Register new user
   */
  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      
      // New flow: registration requires email verification
      if (response.data.requires_verification) {
        return { success: true, requiresVerification: true, message: response.data.message };
      }
      
      // Fallback: legacy flow with immediate token
      const { access_token, user: newUser } = response.data;
      const verified = await completeLogin(access_token, newUser);
      if (!verified) {
        return { success: false, error: 'Could not establish session' };
      }

      return { success: true, user: newUser };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      return { success: false, error: message };
    }
  };

  /**
   * Logout user
   */
  const logout = useCallback(() => {
    // Best-effort server-side token revocation — don't block on failure
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {}); // fire-and-forget
    }
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
    posthogAnalytics.reset();
  }, []);

  /**
   * Update user profile
   */
  const updateProfile = async (updates) => {
    try {
      const response = await api.put('/auth/me', updates);
      
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Update failed';
      return { success: false, error: message };
    }
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback((role) => {
    if (!user) return false;
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    return user.role === role;
  }, [user]);

  /**
   * Check if user is admin or owner
   */
  const isAdmin = useCallback(() => {
    return hasRole(['admin', 'owner']);
  }, [hasRole]);

  /**
   * Check if user is super admin
   */
  const isSuperAdmin = useCallback(() => {
    return user?.internal_role === 'super_admin';
  }, [user]);

  /**
   * Check if the current user holds a specific feature permission.
   * Internal staff and Owner receive all permissions from the backend.
   * @param {string} key - Permission key, e.g. 'campaigns:manage'
   */
  const hasPermission = useCallback((key) => {
    if (!user) return false;
    const perms = user.permissions;
    if (!Array.isArray(perms)) return false;
    return perms.includes(key);
  }, [user]);

  /**
   * Check if the current user holds any of the given permission keys.
   * @param {string[]} keys
   */
  const hasAnyPermission = useCallback((keys) => {
    if (!user || !Array.isArray(user.permissions)) return false;
    const set = new Set(user.permissions);
    return (keys || []).some((k) => set.has(k));
  }, [user]);

  /**
   * The user's account-visibility scope ('all' | 'scoped').
   */
  const dataScope = user?.data_scope || 'all';

  // Verify auth on mount
  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  const value = {
    // State
    user,
    loading,
    isAuthenticated,
    
    // Methods
    login,
    logout,
    register,
    completeLogin,
    updateProfile,
    verifyAuth,
    
    // Role checks
    hasRole,
    isAdmin,
    isSuperAdmin,

    // Permission checks (RBAC v2)
    hasPermission,
    hasAnyPermission,
    dataScope,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
